import { useEffect, useState } from 'react';
import type { MachineListRow, WorktreeAssessment, WorktreeRetentionMode } from '@nightshift/ui-types';
import { api } from '../live/api.ts';
import { bytes, relativeTime } from '../lib/format.ts';

const ORDER: Record<string, number> = { remove: 0, slim: 1 };

function size(value: number | null): string {
  return value === null ? 'Not measured' : bytes(value);
}

function decision(row: WorktreeAssessment, mode: WorktreeRetentionMode): string {
  if (row.action === 'remove') return mode === 'on' ? 'Removing' : 'Would remove';
  if (row.action === 'slim') return mode === 'on' ? 'Removing build output' : 'Would remove build output';
  return 'Kept';
}

/** Kept ticket worktrees on this runner, what clean-up would do with each, and what it has done. */
export function MachineStorage({ row, canWrite, now }: { row: MachineListRow; canWrite: boolean; now: number }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // The operator's choice shows at once; the row's mode only changes when the machines list
  // refreshes, and a radio bound to it alone flicked back to the old value in between.
  const [chosen, setChosen] = useState<WorktreeRetentionMode | null>(null);
  const storage = row.storage;
  useEffect(() => {
    if (chosen !== null && storage?.mode === chosen) setChosen(null);
  }, [chosen, storage?.mode]);
  const setMode = async (mode: WorktreeRetentionMode) => {
    setBusy(true);
    setError('');
    setChosen(mode);
    try {
      await api.setMachineStorageMode(row.id, mode);
    } catch (caught) {
      setChosen(null);
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  };
  if (!storage) {
    return <section className="machine-storage" aria-label="Worktree clean-up">
      <h3 className="machine-card-legend">Worktree clean-up</h3>
      <p className="cfg-help">This runner has not reported its worktrees yet.</p>
    </section>;
  }
  const mode = chosen ?? storage.mode;
  const worktrees = [...storage.worktrees].sort((a, b) =>
    (ORDER[a.action ?? ''] ?? 2) - (ORDER[b.action ?? ''] ?? 2) || (b.bytes ?? 0) - (a.bytes ?? 0) || a.ticket.localeCompare(b.ticket));
  const measured = worktrees.reduce((sum, entry) => sum + (entry.bytes ?? 0), 0);
  const removable = worktrees.filter(entry => entry.action === 'remove');
  const slimmable = worktrees.filter(entry => entry.action === 'slim');
  const removableBytes = removable.reduce((sum, entry) => sum + (entry.bytes ?? 0), 0);
  return <section className="machine-storage" aria-label="Worktree clean-up">
    <h3 className="machine-card-legend">Worktree clean-up</h3>
    <p className="cfg-help">
      A finished ticket's worktree is removed an hour after it is Done or Cancelled. An open ticket idle for seven days
      loses only its build output. A worktree holding uncommitted changes, or a Cancelled ticket's unpushed commits, is kept.
    </p>
    <fieldset className="storage-mode" disabled={!canWrite || busy}>
      <legend>Clean-up</legend>
      <label><input type="radio" name={`storage-mode-${row.id}`} checked={mode === 'report'} onChange={() => void setMode('report')} /> Report only</label>
      <label><input type="radio" name={`storage-mode-${row.id}`} checked={mode === 'on'} onChange={() => void setMode('on')} /> Clean up automatically</label>
    </fieldset>
    {error ? <p role="alert">{error}</p> : null}
    <p>
      {worktrees.length} {worktrees.length === 1 ? 'worktree' : 'worktrees'}{measured > 0 ? `, ${bytes(measured)} measured` : ''}.
      {' '}{removable.length > 0 ? `${removable.length} finished (${removableBytes > 0 ? bytes(removableBytes) : 'size not measured yet'}) ${mode === 'on' ? 'being removed' : 'can be removed'}. ` : 'None finished. '}
      {slimmable.length > 0 ? `${slimmable.length} idle ${mode === 'on' ? 'being slimmed' : 'can be slimmed'}.` : ''}
      {storage.reportedAt !== null ? <span className="cfg-help"> Reported {relativeTime(storage.reportedAt, now)}.</span> : null}
    </p>
    {storage.attemptEvidenceBytes !== null ? <p className="cfg-help">Local evidence copies: {bytes(storage.attemptEvidenceBytes)} (removed after 14 days).</p> : null}
    {worktrees.length > 0 ? <details>
      <summary>Worktrees ({worktrees.length})</summary>
      <table className="storage-table">
        <thead><tr><th scope="col">Ticket</th><th scope="col">Size</th><th scope="col">Clean-up</th><th scope="col">Why</th></tr></thead>
        <tbody>{worktrees.map(entry => <tr key={`${entry.project}/${entry.ticket}`}>
          <td><code>{entry.ticketKey ?? entry.ticket}</code>{entry.projectName ? <span className="cfg-help"> {entry.projectName}</span> : null}</td>
          <td>{size(entry.bytes)}</td>
          <td>{decision(entry, mode)}</td>
          <td>{entry.reason}</td>
        </tr>)}</tbody>
      </table>
    </details> : null}
    {storage.receipts.length > 0 ? <details>
      <summary>Recent clean-up ({storage.receipts.length})</summary>
      <ul className="storage-receipts">{storage.receipts.map(receipt => <li key={receipt.id}>
        <code>{receipt.ticket}</code>{' '}
        {receipt.outcome === 'done'
          ? `${receipt.action === 'remove' ? 'removed' : 'build output removed'}${receipt.bytesFreed ? `, ${bytes(receipt.bytesFreed)} freed` : ''}`
          : receipt.outcome === 'kept' ? `kept: ${receipt.detail ?? 'refused'}` : `failed: ${receipt.detail ?? 'unknown error'}`}
        <span className="cfg-help"> {relativeTime(receipt.at, now)}</span>
      </li>)}</ul>
    </details> : null}
  </section>;
}
